import { SignalCellularNullSharp } from '@mui/icons-material';
import _, { cloneDeep, join } from 'lodash';
import { DateTime } from 'luxon';

import { AdBreakType, ChannelType } from '../../enums';
import type { Channel, ChannelMetaData, Item } from '../../models';
import { CustomAttributes } from '../../models/customAttributes';
import { FuelEntityType, FuelPortalService } from '../../services';
import { SchemaType } from '../../store/forms/Asset-Editor/SchemaTypeOptions';
import type { ChannelForm } from '../../store/forms/Channel/ChannelForm';
import calendarUtils from '../../utils/calendarUtils';
import clientUtils from '../../utils/clientUtils';
import { compressionUtils } from '../../utils/compressionUtils';
import { dateUtils } from '../../utils/dateUtils';
import itemUtils from '../../utils/itemUtils';
import { EventForm } from './EventForm';

const FuelCalendarHelper = {
  getEventClassNames(eventInfo: any, eventsData: any[], viewAdBreaks: boolean, channelType, adBreakType) {
    // console.log('onEventClassNames', { eventInfo, eventsData, viewAdBreaks, channelType, adBreakType });
    const toReturn: string[] = [];
    const extendedProps =
      eventInfo.event && eventInfo.event._def && eventInfo.event._def
        ? eventInfo.event._def.extendedProps
        : SignalCellularNullSharp;
    const { displayUIView } = extendedProps;
    if (this.hasOverlap(eventsData, eventInfo.event) && displayUIView !== 'ScheduleDisplayViewer') {
      toReturn.push('is-overlap');
    }
    if (eventInfo.event && eventInfo.event._def && eventInfo.event._def.extendedProps) {
      const p =
        eventInfo.event && eventInfo.event._def && eventInfo.event._def.extendedProps && eventInfo.event._def.extendedProps;

      if (p.attachedFuelDisplayClassName) {
        toReturn.push(eventInfo.event._def.extendedProps.attachedFuelDisplayClassName);
      } else if (p.attachedFuelEntityType) {
        toReturn.push(eventInfo.event._def.extendedProps.attachedFuelEntityType);
      }

      if (p.copyable) {
        toReturn.push('is-copyable');
      }
    }

    if (
      adBreakType &&
      adBreakType.id !== AdBreakType.DragAndDropAdBreak &&
      channelType &&
      channelType.id === ChannelType.Scheduled &&
      viewAdBreaks
    ) {
      toReturn.push('is-ad-break-visible');
    }

    return toReturn;
  },

  async getClipDuration(eventForm: EventForm, item: Item, channelFormValues: any) {
    let newDuration = eventForm.duration;
    const attachedFuelEntityType = clientUtils.getAttachedFuelEntityType(item);
    let newEnd = dateUtils.convertDateStrLocalToTimeZoneSpecificDate(eventForm.end, eventForm.timezone);
    if (newDuration === '00:00:00') {
      newEnd = DateTime.fromISO(eventForm.start).plus({ hours: 1 }).setZone(eventForm.timezone).toISO();
      newDuration = dateUtils.formatDuration(3600);
      return { newDuration, newEnd };
    }
    if (
      eventForm.mode === 'existing' &&
      eventForm.attachedFuelEntityType === 'NestedChannel' &&
      item.source === 'NestedChannel'
    ) {
      return { newDuration, newEnd };
    }
    // aalt 2023-07-26 #441166 do not set clip duration for Image type assets dropped in the scheduler
    if (item && item.entityType === 'Clip' && item.schemaType !== SchemaType.Image) {
      const adConfiguration = null;
      const formValues: ChannelForm = channelFormValues.getValues();
      const data: ChannelMetaData = {};
      data.variantCodes = formValues.defaultVariants;
      if (formValues.showAds) {
        data.adConfiguration = formValues.adsSponsorsForm;
      }
      const durationResponse = await FuelPortalService.getClipDuration(item, data);
      if (durationResponse) {
        let duration = durationResponse.totalExpectedClipRuntime;
        if (duration <= 0) {
          duration = 1; // minimum duration
        }
        newEnd = DateTime.fromISO(eventForm.start).plus({ seconds: duration }).setZone(eventForm.timezone).toISO();
        newDuration = dateUtils.formatDuration(duration);
      }
    }
    return { newDuration, newEnd };
  },

  async getClipOrNestedChannelEvent(eventForm: EventForm, item: Item, channelFormValues: any): Promise<EventForm> {
    const attachedFuelEntityType = clientUtils.getAttachedFuelEntityType(item);
    const { newDuration, newEnd } = await FuelCalendarHelper.getClipDuration(eventForm, item, channelFormValues);
    const attachedFuelEntityId = clientUtils.getAttachedFuelEntityId(item);
    const categories = item && item.categories ? item.categories.map((c) => c.name) : [];
    const description =
      item && item.data && item.data.businessMetadata && item.data.businessMetadata.synopsis
        ? item.data.businessMetadata.synopsis
        : '';
    const customAttributesObj = FuelCalendarHelper.extractCustomAttributesFromTags(item && item.tags ? item.tags : []);
    const customAttributesJson = JSON.stringify(FuelCalendarHelper.removeEmptyValues(customAttributesObj));
    const encodedCustomeAttributesJSON = compressionUtils.base64Encode(customAttributesJson);
    const subTitle = item && item.data && item.data.businessMetadata?.subTitle ? item.data.businessMetadata.subTitle : '';
    const event: EventForm = {
      ...eventForm,
      duration: newDuration,
      end: newEnd,
      title: eventForm.title || item?.title,
      // duration: calendarUtils.getDuration(eventForm.start, newEnd),
      description,
      categories,
      color: calendarUtils.getEventColor(attachedFuelEntityType, item && item.source ? item.source : ''),
      attachedFuelEntityType,
      attachedFuelEntityId,
      attachedFuelDisplayClassName: item && item.source ? item.source : '',
      customAttributes: CustomAttributes.loadCustomAttributes(customAttributesObj),
      attachedFuelCustomAttributes: encodedCustomeAttributesJSON,
      subTitle,
    };
    return event;
  },
  hasOverlap(events, event) {
    if (events && events.length > 0) {
      // console.log('events.length', events.length);
      for (let i = 0; i < events.length; i++) {
        const element = events[i];
        const isOverlap = FuelCalendarHelper.hasOverlapEvent(event, element);
        if (isOverlap) {
          return true;
        }
      }
    }
    return false;
  },
  hasOverlapEvent(event, event2) {
    if (event2.id !== event.id && event2.start >= event.start && event2.start < event.end) {
      return true;
    }
    return false;
  },

  async showNestedChannelWarning(eventToCheck) {
    // console.log('showNestedChannelWarning', { eventToCheck });
    if (eventToCheck.item && eventToCheck.item.source == 'NestedChannel') {
      const { item } = eventToCheck;
      // we don't need fullInfo for the nested channel, just the metaData.channelContentLimits.maxClipDuration
      const nestedChannel = await FuelPortalService.getEntityById<Channel>(FuelEntityType.Channel, item.id);

      if (
        eventToCheck &&
        eventToCheck.duration &&
        nestedChannel &&
        nestedChannel.metaData &&
        nestedChannel.metaData.channelContentLimits &&
        nestedChannel.metaData.channelContentLimits.maxClipDuration
      ) {
        const m = nestedChannel.metaData.channelContentLimits.maxClipDuration;
        const maxClipDuration = clientUtils.convertTimestampToNumber(m);
        const eventDuration = clientUtils.convertTimestampToNumber(eventToCheck.duration);
        // console.log('showNestedChannelWarning', { maxClipDuration, eventDuration });
        if (maxClipDuration != null && eventDuration != null && maxClipDuration > 0 && maxClipDuration > eventDuration) {
          return true;
        }
      }
    }
    return false;
  },

  checkNewEvent(eventToCheck) {
    const startMoment = DateTime.fromISO(eventToCheck.start);
    const pastDate = DateTime.now().minus({ hours: 24 });
    const isBefore = pastDate > startMoment;
    if (isBefore) {
      return false;
    }
    return true;
  },
  isAnOverlapEvent(allevents, eventToCheck, excludeDate, timezone) {
    const startMoment = DateTime.fromISO(eventToCheck.start);
    const endMoment = DateTime.fromISO(eventToCheck.end);
    try {
      for (let i = 0; i < allevents.length; i++) {
        const eventA = allevents[i];
        const eventAStart = DateTime.fromISO(eventA.start);
        const eventAEnd = DateTime.fromISO(eventA.end);
        if (eventA.id === eventToCheck.id) {
          return false;
        }
        //
        if (dateUtils.convertDateStrLocalToTimeZoneSpecificDate(eventToCheck.start, timezone) === excludeDate) {
          return false;
        }
        if (eventToCheck.exdate.includes(eventA.start)) {
          return false;
        }
        if (startMoment > eventAStart && eventAEnd > startMoment) {
          // console.log('start-time in between any of the events');
          return true;
        }
        // end-time in between any of the events
        if (endMoment > eventAStart && eventAEnd > endMoment) {
          // console.log('end-time in between any of the events');
          return true;
        }
        // any of the events in between/on the start-time and end-time
        if (startMoment <= eventAStart && endMoment >= eventAEnd) {
          // console.log('any of the events in between/on the start-time and end-time');
          return true;
        }
        // added extra checking if both start date are same
        if (FuelCalendarHelper.hasOverlapEvent(eventA, eventToCheck)) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error(error);
      // throw error;
    }
  },

  extractValidTypeIds(data, validTypes) {
    return data
      .filter((item) => {
        const typePart = clientUtils.parseFuelEntityType(item.attachedFuelEntityId);
        return validTypes.includes(typePart);
      })
      .map((item) => clientUtils.parseFuelEntityId(item.attachedFuelEntityId));
  },

  /**
   * Does the work of snapping event to the closest event near it.
   * thresholdMinutes = minutes surrounding event to snap event to. eg 30 means if an event starts
   * within 30 minutes of start or end to snap to that event too.
   */
  snapEvent(eventForm: EventForm, allevents: EventForm[], timezone: string) {
    const eventFormCloine = _.cloneDeep(eventForm);
    // console.log('snapEvent', { eventFormCloine, allevents, timezone });
    const orderedEvents = _.sortBy(allevents, (e) => e.start);
    // console.log('snapEvent', { orderedEvents });
    const startMoment = DateTime.fromISO(eventForm.start).valueOf();
    const startWithOffset = DateTime.fromISO(eventForm.start).minus({ minutes: 30 }).valueOf();
    const endMoment = DateTime.fromISO(eventForm.end).valueOf();
    const endWithOffset = DateTime.fromISO(eventForm.end).plus({ minutes: 30 }).valueOf();
    const diff = dateUtils.getDifferenceDurationByUnit(eventForm.end, eventForm.start, 'milliseconds');
    let closestEventIndex = -1;
    for (let i = 0; i < orderedEvents.length; i++) {
      const eventA = orderedEvents[i];
      if (eventA.id === eventForm.id || (eventForm.old_id && eventA.id === eventForm.old_id)) {
        continue;
      }
      // const eventEnd = eventA.end.getTime();
      const eventEnd = DateTime.fromJSDate(eventA.end).valueOf();
      const eventStart = DateTime.fromJSDate(eventA.start).valueOf();
      if (startMoment >= eventStart && startMoment <= eventEnd) {
        // if the event overlaps at start
        // console.log('snapEvent: event overlapped with start.', { startMoment, eventStart, eventEnd });
        closestEventIndex = i;
        break; // exit the loop at the first match
      } else if (endMoment >= eventStart && endMoment <= eventEnd) {
        // if the event overlaps at end
        // console.log('snapEvent: event overlapped with end. ', { endMoment, eventStart, eventEnd });
        closestEventIndex = i;
        break; // exit the loop at the first match
      } else if (startMoment <= eventStart && endMoment >= eventEnd) {
        // if the event overlaps entire event
        // console.log('snapEvent: event overlapped entire event.', eventA.title, { eventA, eventForm });
        closestEventIndex = i;
        break; // exit the loop at the first match
      } else if (startMoment >= eventEnd && startWithOffset <= eventEnd) {
        // console.log('snapEvent: event start within 30 minutes of another event end.', { startWithOffset, startMoment, eventEnd });
        closestEventIndex = i;
        break; // exit the loop at the first match
      } else if (endMoment <= eventStart && endWithOffset >= eventStart) {
        // console.log('snapEvent: event end within 30 minutes of another event start.', { endWithOffset, endMoment, eventEnd });
        closestEventIndex = i;
        break; // exit the loop at the first match
      }
    }

    if (closestEventIndex === -1) {
      // console.log('snapEvent: no snap event found within 30 minutes of start or end, snapping to nearest half hour!');
      const minutes = DateTime.fromISO(eventForm.start).minute % 30;
      const r = minutes % 30;
      // console.log('snapEvent', { r, minutes, second: DateTime.fromISO(eventForm.start).second});
      if (r !== 0 || DateTime.fromISO(eventForm.start).second !== 0) {
        let newStart;
        let newEnd;
        if (r > 15) {
          newStart = DateTime.fromISO(eventForm.start)
            .plus({ minutes: 30 - r })
            .set({ second: 0 })
            .setZone(eventForm.timezone)
            .toISO();
          newEnd = DateTime.fromISO(eventForm.start)
            .plus({ minutes: 30 - r })
            .set({ second: 0 })
            .plus({ milliseconds: diff })
            .setZone(eventForm.timezone)
            .toISO();
        } else {
          newStart = DateTime.fromISO(eventForm.start)
            .minus({ minutes: r })
            .set({ second: 0 })
            .setZone(eventForm.timezone)
            .toISO();
          newEnd = DateTime.fromISO(eventForm.start)
            .minus({ minutes: 30 })
            .set({ second: 0 })
            .plus({ milliseconds: diff })
            .setZone(eventForm.timezone)
            .toISO();
        }

        eventForm.start = newStart;
        eventForm.end = newEnd;
      }
      return eventForm;
    }
    const closestEvent = orderedEvents[closestEventIndex];

    let blockStart = closestEvent.start;
    let blockEnd = closestEvent.end;
    // here is where we detect if the pre or post event detected above is part of a block of events
    for (let i = closestEventIndex; i < orderedEvents.length; i++) {
      // don't exceed loaded events bounds
      if (i + 1 >= orderedEvents.length) {
        break;
      }
      const nextEvent = orderedEvents[i + 1];
      if (nextEvent.id === eventForm.id || (eventForm.old_id && nextEvent.id === eventForm.old_id)) {
        continue;
      }
      const diffToNextEvent = dateUtils.getDifferenceDurationByUnit(nextEvent.start, blockEnd, 'milliseconds'); //
      if (diffToNextEvent > 3000) {
        break;
      }

      if (DateTime.fromJSDate(nextEvent.start).minus({ minutes: 180 }) > DateTime.fromJSDate(closestEvent.end)) {
        blockEnd = closestEvent.end;
        break;
      }

      blockEnd = nextEvent.end;
    }
    for (let i = closestEventIndex; i < orderedEvents.length; i--) {
      // don't exceed loaded events bounds
      if (i - 1 < 0) {
        break;
      }

      const nextEvent = orderedEvents[i - 1];
      if (nextEvent.id === eventForm.id || (eventForm.old_id && nextEvent.id === eventForm.old_id)) {
        continue;
      }
      const diffToNextEvent = Math.abs(dateUtils.getDifferenceDurationByUnit(nextEvent.end, blockStart, 'milliseconds'));
      if (diffToNextEvent > 3000) {
        break;
      }
      if (DateTime.fromJSDate(nextEvent.start).plus({ minutes: 180 }) < DateTime.fromJSDate(closestEvent.start)) {
        blockStart = closestEvent.start;
        break;
      }

      blockStart = nextEvent.start;
    }

    let lockToStart = false;
    const diffToStart = Math.abs(dateUtils.getDifferenceDurationByUnit(eventForm.start, blockStart, 'milliseconds'));
    const diffToEnd = Math.abs(dateUtils.getDifferenceDurationByUnit(eventForm.start, blockEnd, 'milliseconds'));
    if (diffToEnd > diffToStart) {
      lockToStart = true;
    }

    if (lockToStart) {
      const newStart = DateTime.fromJSDate(blockStart).minus({ milliseconds: diff }).setZone(eventForm.timezone).toISO();
      const newEnd = dateUtils.convertDateLocalToTimeZoneSpecificJSDate(blockStart, eventForm.timezone, false);
      eventForm.start = newStart;
      eventForm.end = newEnd;
    } else {
      const newStart = dateUtils.convertDateLocalToTimeZoneSpecificJSDate(blockEnd, eventForm.timezone, false);
      const newEnd = DateTime.fromJSDate(blockEnd).plus({ milliseconds: diff }).setZone(eventForm.timezone).toISO();

      eventForm.start = newStart;
      eventForm.end = newEnd;
    }

    return eventForm;
  },
  extractCustomAttributesFromTags(tags: string[]) {
    const myState = cloneDeep(CustomAttributes.Create());
    // console.log('refreshLongFormFields', tags);
    for (const fieldKey in myState) {
      // console.log('refreshLongFormFields fieldKey', fieldKey);
      const myvalues = [];
      if (tags && tags.length > 0) {
        for (let i = 0; i < tags.length; i++) {
          const tag = tags[i];
          if (itemUtils.isMetaTag(tag)) {
            const { key, value } = itemUtils.getMetaTagKeyValue(tag);
            if (key === fieldKey || (fieldKey === 'actor' && key === 'cast')) {
              myvalues.push(value);
            }
          }
        }
      }

      let fieldValue = join(myvalues, ', ');
      // just take the first tag for these special one liner metatags
      if (myvalues.length > 1 && itemUtils.isSingleValueTagField(fieldKey)) {
        fieldValue = myvalues[0];
      }
      myState[fieldKey] = fieldValue;
    }
    return myState;
  },
  getNewEvent(timezone, showRecurring: boolean = false, eventUsedFor = 'Channel', channelType = ChannelType.Scheduled) {
    const date = new Date();
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours(), 0, 0);
    const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours() + 1, 0, 0);
    const allowCustomTextEditable = channelType !== ChannelType.Scheduled;
    const newEvent = EventForm.create(
      dateUtils.convertDateLocalToTimeZoneSpecificJSDate(startDate, timezone, false),
      dateUtils.convertDateLocalToTimeZoneSpecificJSDate(endDate, timezone, false),
      timezone,
      showRecurring,
      eventUsedFor,
      allowCustomTextEditable
    );
    return newEvent;
  },
  exportIcs(events, timezone) {
    if (events.length > 0) {
      const icsStr = calendarUtils.getICSString(events, timezone);
      // console.log('icsStr', icsStr);
      window.open(`data:text/calendar;charset=utf8,${escape(icsStr)}`);
    }
  },
  removeEmptyValues(obj) {
    return Object.entries(obj)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  },
};

export default FuelCalendarHelper;
